import * as React from "react";
import { Check, ChevronDown, Copy, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import type { Brand } from "@/data/config";
import { usePortal } from "@/features/portal/portal-context";
import { brandPath, sectionOf } from "@/features/portal/navigation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DeleteBrandDialog } from "@/features/portal/delete-brand-dialog";
import { HoldToDeleteBrandDialog } from "@/features/portal/hold-to-delete-brand-dialog";

function Avatar({ brand, size = 30 }: { brand: Brand; size?: number }) {
  return (
    <div
      className="grid shrink-0 place-items-center rounded-[9px] bg-brand-gradient font-extrabold text-white"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {brand.initials}
    </div>
  );
}

export function BrandSwitcher() {
  const { brands, activeId, activeBrand, viewingOthersBrand, deleteBrand, canCreateBrand, dirty } =
    usePortal();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Two-stage delete: step 1 types the name, step 2 holds the trashcan.
  const [deleting, setDeleting] = React.useState<Brand | null>(null);
  const [confirming, setConfirming] = React.useState<Brand | null>(null);
  if (!activeBrand) return null;

  // Switching brands keeps you on the same section but resets to the new brand's
  // live theme (drop `?theme`); BrandRoute applies the selection from the URL.
  function switchTo(id: string) {
    navigate(brandPath(id, sectionOf(pathname)));
  }

  const isOwner = activeBrand.viewerRole === "OWNER";

  function copyBrandId(id: string) {
    navigator.clipboard?.writeText(id).then(
      () => toast.success("Brand ID copied"),
      () => toast.error("Couldn’t copy to clipboard"),
    );
  }

  // Split the picker: brands you belong to vs. ones you're only browsing as a
  // super admin (viewerRole null). The admin-opened brand is cached into the
  // same list, so without this it would sit under "Your brands" and read as one
  // of yours.
  const myBrands = brands.filter((b) => b.viewerRole != null);
  const viewingBrands = brands.filter((b) => b.viewerRole == null);

  function renderItem(b: Brand) {
    const active = b.id === activeId;
    const foreign = b.viewerRole == null;
    return (
      <DropdownMenuItem key={b.id} onSelect={() => switchTo(b.id)} className="items-start">
        <Avatar brand={b} size={26} />
        <div className="min-w-0 flex-1">
          <span className="text-[13px] font-semibold">{b.name}</span>
          {/* For a brand you don't own, attribute it to its owner rather than
              implying it's yours. */}
          {foreign && b.ownerName && (
            <div className="mt-0.5 text-[11px] text-text-3">Owned by {b.ownerName}</div>
          )}
          {/* Each brand exposes its ID inline — needed for API / embed integration. */}
          <div className="mt-1 flex items-center gap-1.5">
            <code className="min-w-0 flex-1 truncate font-mono text-[11px] text-text-3">{b.id}</code>
            <button
              type="button"
              title="Copy brand ID"
              onClick={(e) => {
                // Don't let the click switch brands or close the menu.
                e.preventDefault();
                e.stopPropagation();
                copyBrandId(b.id);
              }}
              className="inline-flex shrink-0 items-center rounded-md border border-border bg-panel-2 p-0.5 text-text-3 transition-colors hover:text-brand"
            >
              <Copy className="size-3" />
            </button>
          </div>
        </div>
        {active && (
          <Check
            className={cn(
              "size-[15px] shrink-0 self-start",
              foreign ? "text-[#b9851f] dark:text-[#f5b945]" : "text-brand",
            )}
          />
        )}
      </DropdownMenuItem>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger className="flex w-full cursor-pointer items-center gap-2.5 rounded-xl border border-border bg-panel-2 p-[9px_10px] text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring">
          <Avatar brand={activeBrand} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 text-[13.5px] font-bold text-foreground">
              <span className="truncate">{activeBrand.name}</span>
              {viewingOthersBrand && (
                <span className="shrink-0 whitespace-nowrap rounded-[5px] bg-[rgba(245,185,69,.16)] px-1.5 py-px text-[9px] font-extrabold tracking-[.04em] text-[#b9851f] dark:text-[#f5b945]">
                  ADMIN VIEW
                </span>
              )}
              {dirty && (
                <span
                  title="Unsaved changes"
                  className="size-[7px] shrink-0 rounded-full bg-[#f5b945]"
                />
              )}
            </div>
            <div className="truncate text-[11px] text-text-3">
              {viewingOthersBrand && activeBrand.ownerName
                ? `Owned by ${activeBrand.ownerName}`
                : activeBrand.plan}
            </div>
          </div>
          <ChevronDown className="size-4 text-text-3" />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
          {myBrands.length > 0 && (
            <>
              <DropdownMenuLabel>Your brands</DropdownMenuLabel>
              {myBrands.map(renderItem)}
            </>
          )}
          {viewingBrands.length > 0 && (
            <>
              {myBrands.length > 0 && <DropdownMenuSeparator />}
              <DropdownMenuLabel className="flex items-center gap-1.5 text-[#b9851f] dark:text-[#f5b945]">
                <ShieldCheck className="size-3" /> Viewing as admin
              </DropdownMenuLabel>
              {viewingBrands.map(renderItem)}
            </>
          )}
          {canCreateBrand && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => navigate("/create")} className="text-muted-foreground">
                <Plus className="size-4" /> Create a brand
              </DropdownMenuItem>
            </>
          )}
          {isOwner && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => setDeleting(activeBrand)}
                className="text-[#ff6b6b] focus:bg-[#ff6b6b]/10"
              >
                <Trash2 className="size-4" /> Delete “{activeBrand.name}”
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Step 1 — type the brand name. On confirm, hand off to the hold gesture. */}
      <DeleteBrandDialog
        brand={deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        onConfirm={(brand) => {
          setDeleting(null);
          // Let the first dialog finish closing before opening the second, so
          // the two modals don't fight over focus / scroll lock.
          window.setTimeout(() => setConfirming(brand), 160);
        }}
      />

      {/* Step 2 — press and hold the trashcan to actually delete. */}
      <HoldToDeleteBrandDialog
        brand={confirming}
        onOpenChange={(open) => !open && setConfirming(null)}
        onConfirm={(brand) => {
          setConfirming(null);
          // Navigate to whatever brand remains (or the create flow if none).
          void deleteBrand(brand.id).then((nextId) => navigate(nextId ? brandPath(nextId) : "/create"));
        }}
      />
    </>
  );
}
